import { Attachment } from 'stream-chat'
import {ChatFileTypes} from '../types'

export const getAttachmentLabel = (attachment: Attachment) => {
  switch (attachment.type) {
    case 'file':
      return '📎'
    case 'video':
      return '🎥'
    case 'image':
      return '📷'
    case 'giphy':
      return '🖼️'
    case 'voiceRecording':
      return '🎤'
    default:
      return 'New message'
  }
}

export function formatPlaybackTime(milliseconds: number) {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export const getAttachmentFallbackText = (attachment: Attachment) => {
  switch (attachment.type) {
    case 'file':
      return `📎 ${attachment.title || 'File'}`
    case 'video':
      return `🎥 ${attachment.title || 'Video'}`
    case 'image':
      return '📷 Photo'
    case 'giphy':
      return '🖼️ GIF'
    case 'voiceRecording':
      return `🎤 ${formatPlaybackTime(attachment?.duration * 1000)}`
    default:
      return attachment.title || 'New message'
  }
}

export function getAttachmentExtension(attachment: Attachment): string {
  if (attachment.mime_type) {
    const mimeToExt: Record<string, string> = {
      'application/pdf': '.pdf',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        '.docx',
      'application/vnd.ms-excel': '.xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        '.xlsx',
      'application/vnd.ms-powerpoint': '.ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        '.pptx',
      'text/plain': '.txt',
      'text/csv': '.csv',
      'application/json': '.json',
      'application/xml': '.xml',
      'application/zip': '.zip',
      'application/x-rar-compressed': '.rar',
      'image/jpeg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'video/mp4': '.mp4',
      'video/quicktime': '.mov',
      'audio/mpeg': '.mp3',
      'audio/wav': '.wav',
    }

    if (mimeToExt[attachment.mime_type]) {
      return mimeToExt[attachment.mime_type]
    }
  }

  const urlToCheck =
    attachment.asset_url || attachment.image_url || (attachment as any).url
  if (urlToCheck) {
    try {
      const urlWithoutQuery = urlToCheck.split('?')[0]
      const urlParts = urlWithoutQuery.split('.')
      if (urlParts.length > 1) {
        const extension = urlParts[urlParts.length - 1].toLowerCase()
        if (extension && extension.length <= 5) {
          return `.${extension}`
        }
      }

      const urlParams = new URL(urlToCheck).searchParams
      for (const [, value] of urlParams.entries()) {
        if (value.includes('.')) {
          const valueParts = value.split('.')
          if (valueParts.length > 1) {
            const extension = valueParts[valueParts.length - 1].toLowerCase()
            if (
              extension &&
              extension.length <= 5 &&
              /^[a-z0-9]+$/.test(extension)
            ) {
              return `.${extension}`
            }
          }
        }
      }
    } catch (err) {}
  }

  if (attachment.title || (attachment as any).name) {
    const fileName = attachment.title || (attachment as any).name || ''
    if (fileName && typeof fileName === 'string') {
      const dotIndex = fileName.lastIndexOf('.')
      if (dotIndex > 0 && dotIndex < fileName.length - 1) {
        return fileName.substring(dotIndex)
      }
    }
  }

  if (
    (attachment.image_url || (attachment as any).url) &&
    !attachment.asset_url
  ) {
    return '.jpg'
  }

  return '.bin'
}

const fileTypeToTextMap: Record<string, string> = {
  file: 'File',
  image: 'Image',
  video: 'Video',
  giphy: 'GIF',
  audio: 'Audio',
  voiceRecording: 'Voice message',
}

const fileTypeToIconMap: Record<string, string> = {
  file: 'file-06',
  image: 'image-01',
  video: 'video-recorder',
  giphy: 'sticker-circle',
  audio: 'microphone-01',
  voiceRecording: 'microphone-01',
}

export function attachmentIcon(attachment?: Attachment) {
  return fileTypeToIconMap[attachment?.type]
}

export function attachmentPreviewText(attachment?: Attachment) {
  if (!attachment) return null
  const fileType = attachment.type
  if (!fileType) return null
  const extension = getAttachmentExtension(attachment)
  const label = fileTypeToTextMap[fileType]
  if (!label) return null
  const typesWithoutExtension = [
    ChatFileTypes.Image,
    ChatFileTypes.Audio,
    ChatFileTypes.VoiceRecording,
  ]
  return typesWithoutExtension.includes(fileType as ChatFileTypes)
    ? label
    : `${label}${extension}`
}